import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as XLSX from "xlsx";
import { Table, Tbody, Td, Th, Thead, Tr } from "react-super-responsive-table";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import Button from "../components/UiElements/Button";
import Chip from "../components/UiElements/Chip";
import FeedbackEditIcon from "../components/icons/FeedbackEditIcon";
import ResetIcon from "../components/icons/ResetIcon";
import AddIcon from "../components/icons/AddIcon";
import XCloseIcon from "../components/icons/XCloseIcon";
import ExcelIcon from "../components/icons/ExcelIcon";
import TestDriveFeedbackEditor, {
  type ExperienceForm,
  type FormOption,
  type FormQuestion,
  STAR_RATING_OPTIONS,
  getFeedbackFormDisplayName,
} from "../components/TestDriveFeedbackEditor";
import TestDriveCarRowActions from "../components/TestDriveCarRowActions";
import Input from "../components/UiElements/Input";
import ErrorMessage from "../components/UiElements/ErrorMessage";
import DropdownSelect from "../components/DropdownSelect/DropdownSelect";
import config from "../config";
import { tokens } from "../design-system/tokens";
import useIsMobile from "../hooks/useIsMobile";
import styles from "./TestDriveList.module.css";

type StatusType = "available" | "pending" | "occupied";

type FeedbackAnswer = {
  questionKey: string;
  questionLabel: string;
  questionType: "singleSelect" | "text" | "starRating";
  selectedOptionValue: string | null;
  selectedOptionLabel: string | null;
  freeText: string | null;
};

type ExperienceFeedback = {
  formVersion: number;
  submittedAt: string | null;
  answers: FeedbackAnswer[];
};

type Driver = {
  userId?: string | null;
  name: string;
  phone: string;
  personalId?: string | null;
  startDate: string;
  endDate?: string | null;
};

type TestDriveSession = Driver & {
  experienceFeedback?: ExperienceFeedback | null;
};

type TestDriveCar = {
  _id: string;
  carNumber: string;
  manufacturer: string | null;
  model: string | null;
  status: StatusType;
  nextFeedbackFormVersion?: number | null;
  currentDriver: Driver | null;
  testDriveHistory: TestDriveSession[];
};

type EndFormState = {
  [questionKey: string]: {
    selectedOptionValue?: string;
    freeText?: string;
  };
};

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${config.backendUrl}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      ...(init?.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || "Request failed");
  }
  return data as T;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "כל הסטטוסים" },
  { value: "available", label: "פנוי" },
  { value: "pending", label: "ממתין לאישור" },
  { value: "occupied", label: "בתפוסה" },
] as const;

function getTotalDaysHeld(startDate?: string, endDate?: string | null): number | null {
  if (!startDate) {
    return null;
  }
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) {
    return null;
  }
  const end = endDate ? new Date(endDate) : new Date();
  if (Number.isNaN(end.getTime())) {
    return null;
  }
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) {
    return null;
  }
  return Math.ceil(diffMs / MS_PER_DAY);
}

function formatTotalDaysHeld(startDate?: string, endDate?: string | null): string {
  const days = getTotalDaysHeld(startDate, endDate);
  if (days === null) {
    return "-";
  }
  if (days === 1) {
    return "יום אחד";
  }
  return `${days} ימים`;
}

function toLocalDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function oldestHistoryStartDateInput(cars: TestDriveCar[]): string {
  let oldestMs: number | null = null;
  for (const car of cars) {
    for (const session of car.testDriveHistory || []) {
      if (!session?.startDate) continue;
      const t = new Date(session.startDate).getTime();
      if (Number.isNaN(t)) continue;
      if (oldestMs === null || t < oldestMs) {
        oldestMs = t;
      }
    }
  }
  return toLocalDateInputValue(oldestMs === null ? new Date() : new Date(oldestMs));
}

function getHistorySessionUserId(session: TestDriveSession): string {
  const raw = session.userId as string | { toString?: () => string } | undefined;
  if (raw && typeof raw === "object" && typeof raw.toString === "function") {
    return raw.toString();
  }
  return String(raw ?? "");
}

function isActiveHistorySession(car: TestDriveCar, session: TestDriveSession): boolean {
  if (car.status !== "occupied" || !car.currentDriver || session.endDate) {
    return false;
  }
  const sessionUserId = getHistorySessionUserId(session);
  const driverUserId = getHistorySessionUserId(car.currentDriver as TestDriveSession);
  if (sessionUserId !== driverUserId) {
    return false;
  }
  return (
    new Date(car.currentDriver.startDate).getTime() ===
    new Date(session.startDate).getTime()
  );
}

function isFeedbackNotSubmitted(session: TestDriveSession): boolean {
  return !session.experienceFeedback?.submittedAt;
}

function canSendFeedbackReminder(car: TestDriveCar, session: TestDriveSession): boolean {
  return (
    Boolean(session.endDate) &&
    isFeedbackNotSubmitted(session) &&
    !isActiveHistorySession(car, session)
  );
}

function feedbackReminderSessionKey(session: TestDriveSession): string {
  return `${getHistorySessionUserId(session)}-${session.startDate}`;
}

type HistorySessionDeleteTarget = {
  userId: string;
  startDate: string;
  driverName: string;
  isActive: boolean;
};

const TestDriveList: React.FC = () => {
  const [cars, setCars] = useState<TestDriveCar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | StatusType>("all");

  const [endDriveForm, setEndDriveForm] = useState<ExperienceForm | null>(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalError, setAddModalError] = useState("");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [exportingHistory, setExportingHistory] = useState(false);
  const [exportCar, setExportCar] = useState<TestDriveCar | null>(null);
  const [startModalError, setStartModalError] = useState("");
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteFormVersionModalOpen, setDeleteFormVersionModalOpen] = useState(false);
  const [formVersionPendingDelete, setFormVersionPendingDelete] = useState<number | null>(null);
  const [deleteHistorySessionModalOpen, setDeleteHistorySessionModalOpen] = useState(false);
  const [historySessionPendingDelete, setHistorySessionPendingDelete] =
    useState<HistorySessionDeleteTarget | null>(null);
  const [historySearch, setHistorySearch] = useState("");
  const [feedbackReminderSendingKey, setFeedbackReminderSendingKey] = useState<string | null>(
    null,
  );
  const isMobile = useIsMobile();

  const [selectedCar, setSelectedCar] = useState<TestDriveCar | null>(null);

  const [addForm, setAddForm] = useState({
    carNumber: "",
    manufacturer: "",
    model: "",
  });

  const [startForm, setStartForm] = useState({
    name: "",
    phone: "",
    personalId: "",
  });
  const startDriverLookupRef = useRef(0);

  type LookupDriver = {
    displayName: string | null;
    name: string | null;
    personalId: string | null;
    phone: string | null;
  };

  const applyDriverToStartForm = (driver: LookupDriver) => {
    const driverName = (driver.displayName || driver.name || "").trim();
    const personalId = (driver.personalId || "").trim();
    const phone = (driver.phone || "").trim();
    setStartForm((prev) => ({
      ...prev,
      ...(driverName ? { name: driverName } : {}),
      ...(personalId ? { personalId } : {}),
      ...(phone ? { phone } : {}),
    }));
  };

  const [endFormAnswers, setEndFormAnswers] = useState<EndFormState>({});
  const [feedbackFormVersion, setFeedbackFormVersion] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsError, setFormsError] = useState("");
  const [forms, setForms] = useState<ExperienceForm[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [editorQuestions, setEditorQuestions] = useState<FormQuestion[]>([]);
  const [editorFormName, setEditorFormName] = useState("");

  const headingStyle: React.CSSProperties = {
    ...tokens.typography.h2,
    color: tokens.colors.text.secondary,
  };

  const tableCellStyle: React.CSSProperties = {
    ...tokens.typography.bodySmall,
    color: tokens.colors.text.primary,
  };

  const dialogSlotProps = {
    paper: {
      sx: { borderRadius: tokens.borderRadius.xxl },
    },
  };

  const feedbackDialogSlotProps = {
    paper: {
      sx: {
        borderRadius: tokens.borderRadius.xxl,
        minHeight: 360,
      },
    },
  };

  const historyDialogSlotProps = {
    paper: {
      className: styles.historyDialogPaper,
      sx: {
        borderRadius: tokens.borderRadius.xxl,
        height: "calc(100% - 64px)",
        maxHeight: "calc(100% - 64px)",
        display: "flex",
        flexDirection: "column",
        "@media (max-width: 600px)": {
          height: "calc(100% - 32px)",
          maxHeight: "calc(100% - 32px)",
        },
      },
    },
  };

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ event: string; car: TestDriveCar }>;
      const { event: updateEvent, car } = custom.detail || {};
      if (!car?.carNumber) return;
      setCars((prev) => {
        if (updateEvent === "deleted") {
          return prev.filter((c) => c.carNumber !== car.carNumber);
        }
        const idx = prev.findIndex((c) => c.carNumber === car.carNumber);
        if (idx === -1) return prev;
        const next = [...prev];
        next[idx] = car;
        return next;
      });
      if (updateEvent === "deleted") {
        setSelectedCar((current) =>
          current?.carNumber === car.carNumber ? null : current,
        );
      }
    };
    window.addEventListener("testDriveUpdated", handler);
    return () => window.removeEventListener("testDriveUpdated", handler);
  }, []);

  const loadCars = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiRequest<{ cars: TestDriveCar[] }>("/test-drive/cars");
      setCars(data.cars || []);
    } catch (err: any) {
      setError(err?.message || "שגיאה בטעינת נסיעות מבחן");
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultForm = async () => {
    try {
      const data = await apiRequest<{ form: ExperienceForm | null }>(
        "/test-drive/experience-form/default"
      );
      return data.form || null;
    } catch {
      return null;
    }
  };

  const loadFormVersions = async (keepVersion?: number | null) => {
    setFormsLoading(true);
    setFormsError("");
    try {
      const data = await apiRequest<{ forms: ExperienceForm[] }>(
        "/test-drive/experience-form/versions"
      );
      const nextForms = data.forms || [];
      setForms(nextForms);
      if (nextForms.length > 0) {
        const formToSelect =
          (keepVersion != null && nextForms.find((form) => form.version === keepVersion)) ||
          nextForms.find((form) => form.isDefault) ||
          nextForms[0];
        setSelectedVersion(formToSelect.version);
        setEditorQuestions(JSON.parse(JSON.stringify(formToSelect.questions || [])));
        setEditorFormName(getFeedbackFormDisplayName(formToSelect));
      } else {
        setSelectedVersion(null);
        setEditorQuestions([]);
        setEditorFormName("");
      }
    } catch (err: any) {
      setFormsError(err?.message || "שגיאה בטעינת גרסאות משוב");
    } finally {
      setFormsLoading(false);
    }
  };

  const refreshFormsList = async () => {
    try {
      const data = await apiRequest<{ forms: ExperienceForm[] }>(
        "/test-drive/experience-form/versions"
      );
      setForms(data.forms || []);
    } catch {
      // labels fall back to version numbers when forms are unavailable
    }
  };

  useEffect(() => {
    loadCars();
    refreshFormsList();
  }, []);

  const filteredCars = useMemo(() => {
    return cars.filter((car) => {
      const textMatch =
        !search.trim() ||
        [car.carNumber, car.manufacturer || "", car.model || ""]
          .join(" ")
          .toLowerCase()
          .includes(search.trim().toLowerCase());
      const statusMatch = statusFilter === "all" || car.status === statusFilter;
      return textMatch && statusMatch;
    });
  }, [cars, search, statusFilter]);

  const feedbackFormOptions = useMemo(
    () =>
      forms
        .slice()
        .sort((a, b) => b.version - a.version)
        .map((form) => ({
          value: String(form.version),
          label: `${getFeedbackFormDisplayName(form)}${form.isDefault ? " (ברירת מחדל)" : ""}`,
        })),
    [forms],
  );

  const selectedStatusFilter =
    STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) ??
    STATUS_FILTER_OPTIONS[0];

  const selectedFeedbackForm = feedbackFormVersion
    ? feedbackFormOptions.find((option) => option.value === String(feedbackFormVersion))
    : undefined;

  const filteredHistorySessions = useMemo(() => {
    if (!selectedCar?.testDriveHistory?.length) {
      return [];
    }
    const reversed = [...selectedCar.testDriveHistory].reverse();
    const query = historySearch.trim().toLowerCase();
    if (!query) {
      return reversed;
    }
    const queryCompact = query.replace(/\s+/g, "");
    return reversed.filter((session) => {
      const name = (session.name || "").toLowerCase();
      const phone = (session.phone || "").toLowerCase();
      const phoneCompact = phone.replace(/\s+/g, "");
      const personalId = (session.personalId || "").toLowerCase();
      return (
        name.includes(query) ||
        phone.includes(query) ||
        phoneCompact.includes(queryCompact) ||
        personalId.includes(query)
      );
    });
  }, [selectedCar?.testDriveHistory, historySearch]);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
  };

  const selectedForm = forms.find((form) => form.version === selectedVersion) || null;

  const getFormLabelByVersion = (version: number | null | undefined) => {
    if (!version) return "-";
    const form = forms.find((f) => f.version === version);
    return form ? getFeedbackFormDisplayName(form) : `גרסה ${version}`;
  };

  const selectVersion = async (version: number) => {
    setFormsError("");
    try {
      const data = await apiRequest<{ form: ExperienceForm }>(
        `/test-drive/experience-form/${version}`
      );
      setSelectedVersion(version);
      setEditorQuestions(JSON.parse(JSON.stringify(data.form.questions || [])));
      setEditorFormName(getFeedbackFormDisplayName(data.form));
    } catch (err: any) {
      setFormsError(err?.message || "שגיאה בטעינת גרסה");
    }
  };

  const addQuestion = () => {
    const key = `question_${Date.now()}`;
    setEditorQuestions((prev) => [
      ...prev,
      {
        key,
        label: "שאלה חדשה",
        type: "singleSelect",
        required: false,
        options: [{ value: "option_1", label: "אפשרות 1", allowsFreeText: false }],
      },
    ]);
  };

  const updateQuestion = (index: number, patch: Partial<FormQuestion>) => {
    setEditorQuestions((prev) =>
      prev.map((question, questionIndex) =>
        questionIndex === index ? { ...question, ...patch } : question
      )
    );
  };

  const deleteQuestion = (index: number) => {
    setEditorQuestions((prev) => prev.filter((_, questionIndex) => questionIndex !== index));
  };

  const reorderQuestions = (questions: FormQuestion[]) => {
    setEditorQuestions(questions);
  };

  const addOption = (questionIndex: number) => {
    setEditorQuestions((prev) =>
      prev.map((question, idx) => {
        if (idx !== questionIndex) return question;
        const options = question.options || [];
        const usedValues = new Set(options.map((option) => option.value));
        let stamp = Date.now();
        let nextValue = `option_${stamp}`;
        while (usedValues.has(nextValue)) {
          nextValue = `option_${++stamp}`;
        }
        return {
          ...question,
          options: [
            ...options,
            {
              value: nextValue,
              label: `אפשרות ${options.length + 1}`,
              allowsFreeText: false,
            },
          ],
        };
      })
    );
  };

  const updateOption = (
    questionIndex: number,
    optionIndex: number,
    patch: Partial<FormOption>
  ) => {
    setEditorQuestions((prev) =>
      prev.map((question, idx) => {
        if (idx !== questionIndex) return question;
        return {
          ...question,
          options: (question.options || []).map((option, currentOptionIndex) =>
            currentOptionIndex === optionIndex ? { ...option, ...patch } : option
          ),
        };
      })
    );
  };

  const deleteOption = (questionIndex: number, optionIndex: number) => {
    setEditorQuestions((prev) =>
      prev.map((question, idx) => {
        if (idx !== questionIndex) return question;
        return {
          ...question,
          options: (question.options || []).filter((_, currentOptionIndex) => currentOptionIndex !== optionIndex),
        };
      })
    );
  };

  const ensureQuestionKeys = (questions: FormQuestion[]): FormQuestion[] => {
    const usedKeys = new Set<string>();
    let stamp = Date.now();
    return questions.map((question) => {
      const trimmed = question.key?.trim() || "";
      let nextQuestion = question;
      if (trimmed && !usedKeys.has(trimmed)) {
        usedKeys.add(trimmed);
        if (trimmed !== question.key) {
          nextQuestion = { ...question, key: trimmed };
        }
      } else {
        let nextKey = `question_${stamp++}`;
        while (usedKeys.has(nextKey)) {
          nextKey = `question_${stamp++}`;
        }
        usedKeys.add(nextKey);
        nextQuestion = { ...question, key: nextKey };
      }

      if (nextQuestion.type !== "singleSelect" || !nextQuestion.options?.length) {
        return nextQuestion;
      }

      const usedOptionValues = new Set<string>();
      let optionStamp = stamp;
      const options = nextQuestion.options.map((option) => {
        const optionValue = option.value?.trim() || "";
        if (optionValue && !usedOptionValues.has(optionValue)) {
          usedOptionValues.add(optionValue);
          return optionValue === option.value ? option : { ...option, value: optionValue };
        }
        let nextValue = `option_${optionStamp++}`;
        while (usedOptionValues.has(nextValue)) {
          nextValue = `option_${optionStamp++}`;
        }
        usedOptionValues.add(nextValue);
        return { ...option, value: nextValue };
      });
      stamp = optionStamp;
      return { ...nextQuestion, options };
    });
  };

  const validateEditor = (questions: FormQuestion[]) => {
    for (const question of questions) {
      if (!question.label?.trim()) return "לכל שאלה חייבת להיות כותרת";
      if (question.type === "singleSelect" && (!question.options || question.options.length === 0)) {
        return `השאלה "${question.label}" חייבת לכלול אפשרויות`;
      }
      if (question.type === "starRating" && question.options && question.options.length > 0) {
        return `השאלה "${question.label}" מסוג דירוג כוכבים לא צריכה אפשרויות מותאמות`;
      }
    }
    return "";
  };

  const saveVersion = async () => {
    if (!selectedVersion) return;
    const questionsWithKeys = ensureQuestionKeys(editorQuestions);
    const validationError = validateEditor(questionsWithKeys);
    if (validationError) {
      setFormsError(validationError);
      return;
    }
    const trimmedName = editorFormName.trim();
    if (!trimmedName) {
      setFormsError("שם משוב הוא שדה חובה");
      return;
    }
    setSaving(true);
    setFormsError("");
    try {
      const questionsPayload = questionsWithKeys.map((question) =>
        question.type === "starRating" ? { ...question, options: [] } : question
      );
      setEditorQuestions(questionsPayload);
      await apiRequest(`/test-drive/experience-form/${selectedVersion}`, {
        method: "PUT",
        body: JSON.stringify({
          questions: questionsPayload,
          name: trimmedName,
        }),
      });
      await loadFormVersions(selectedVersion);
      await refreshFormsList();
    } catch (err: any) {
      setFormsError(err?.message || "שמירת גרסה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const createNewVersion = async () => {
    setSaving(true);
    setFormsError("");
    try {
      const data = await apiRequest<{ form: ExperienceForm }>(
        "/test-drive/experience-form/new-version",
        {
          method: "POST",
          body: JSON.stringify({ questions: [] }),
        }
      );
      await loadFormVersions(data.form?.version);
      if (data.form?.version) {
        await selectVersion(data.form.version);
      }
    } catch (err: any) {
      setFormsError(err?.message || "יצירת גרסה חדשה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const duplicateCurrentVersion = async () => {
    if (!selectedVersion) return;
    setSaving(true);
    setFormsError("");
    try {
      const data = await apiRequest<{ form: ExperienceForm }>(
        `/test-drive/experience-form/duplicate/${selectedVersion}`,
        { method: "POST" }
      );
      await loadFormVersions(data.form?.version);
      if (data.form?.version) {
        await selectVersion(data.form.version);
      }
    } catch (err: any) {
      setFormsError(err?.message || "שכפול גרסה נכשל");
    } finally {
      setSaving(false);
    }
  };

  const setDefaultVersion = async () => {
    if (!selectedVersion) return;
    setSaving(true);
    setFormsError("");
    try {
      await apiRequest(`/test-drive/experience-form/${selectedVersion}/set-default`, {
        method: "POST",
      });
      await loadFormVersions(selectedVersion);
    } catch (err: any) {
      setFormsError(err?.message || "קביעת ברירת מחדל נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteFormVersionModal = () => {
    if (forms.length <= 1) {
      setFormsError("לא ניתן למחוק את הגרסה האחרונה");
      return;
    }
    if (!selectedVersion) return;
    setFormsError("");
    setFormVersionPendingDelete(selectedVersion);
    setDeleteFormVersionModalOpen(true);
  };

  const deleteFormVersion = async () => {
    if (formVersionPendingDelete == null) return;
    setSaving(true);
    setFormsError("");
    try {
      await apiRequest(`/test-drive/experience-form/${formVersionPendingDelete}`, {
        method: "DELETE",
      });
      setDeleteFormVersionModalOpen(false);
      setFormVersionPendingDelete(null);
      await loadFormVersions();
      await loadCars();
    } catch (err: any) {
      setFormsError(err?.message || "מחיקת גרסה נכשלה");
      setDeleteFormVersionModalOpen(false);
      setFormVersionPendingDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusFilterChange = (selected: { value: string }[]) => {
    const value = selected[0]?.value as "all" | StatusType | undefined;
    if (value) setStatusFilter(value);
  };

  const openStartModal = (car: TestDriveCar) => {
    setSelectedCar(car);
    setStartForm({ name: "", phone: "", personalId: "" });
    setStartModalError("");
    setStartModalOpen(true);
  };

  const closeStartModal = () => {
    setStartModalOpen(false);
    setStartModalError("");
  };

  useEffect(() => {
    if (!startModalOpen) {
      return;
    }

    const digits = startForm.phone.replace(/\D/g, "");
    if (digits.length < 9) {
      return;
    }

    const requestId = ++startDriverLookupRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const data = await apiRequest<{
          exists: boolean;
          driver: LookupDriver | null;
        }>(
          `/test-drive/lookup-driver?phone=${encodeURIComponent(startForm.phone.trim())}`
        );

        if (requestId !== startDriverLookupRef.current || !data.exists || !data.driver) {
          return;
        }

        applyDriverToStartForm(data.driver);
      } catch {
        // Ignore lookup errors; admin can still fill fields manually.
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
      startDriverLookupRef.current += 1;
    };
  }, [startModalOpen, startForm.phone]);

  useEffect(() => {
    if (!startModalOpen) {
      return;
    }

    const nameTrimmed = startForm.name.trim();
    if (nameTrimmed.length < 2) {
      return;
    }

    const requestId = ++startDriverLookupRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const data = await apiRequest<{
          exists: boolean;
          driver: LookupDriver | null;
        }>(
          `/test-drive/lookup-driver?name=${encodeURIComponent(nameTrimmed)}`
        );

        if (requestId !== startDriverLookupRef.current || !data.exists || !data.driver) {
          return;
        }

        applyDriverToStartForm(data.driver);
      } catch {
        // Ignore lookup errors; admin can still fill fields manually.
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
      startDriverLookupRef.current += 1;
    };
  }, [startModalOpen, startForm.name]);

  useEffect(() => {
    if (!startModalOpen) {
      return;
    }

    const idTrimmed = startForm.personalId.trim();
    if (idTrimmed.length < 5) {
      return;
    }

    const requestId = ++startDriverLookupRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const data = await apiRequest<{
          exists: boolean;
          driver: LookupDriver | null;
        }>(
          `/test-drive/lookup-driver?personalId=${encodeURIComponent(idTrimmed)}`
        );

        if (requestId !== startDriverLookupRef.current || !data.exists || !data.driver) {
          return;
        }

        applyDriverToStartForm(data.driver);
      } catch {
        // Ignore lookup errors; admin can still fill fields manually.
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
      startDriverLookupRef.current += 1;
    };
  }, [startModalOpen, startForm.personalId]);

  const forceStartWithoutApproval = async (car: TestDriveCar) => {
    if (
      !window.confirm(
        `להתחיל נסיעת מבחן ללא אישור הנהג?\nרכב ${car.carNumber}${car.currentDriver ? ` • ${car.currentDriver.name}` : ""
        }`
      )
    ) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apiRequest("/test-drive/approve-admin", {
        method: "POST",
        body: JSON.stringify({ carNumber: car.carNumber }),
      });
      await loadCars();
    } catch (err: any) {
      setError(err?.message || "שגיאה בהתחלת נסיעה ללא אישור נהג");
    } finally {
      setSaving(false);
    }
  };

  const cancelPendingDrive = async (car: TestDriveCar) => {
    setSaving(true);
    setError("");
    try {
      await apiRequest("/test-drive/cancel-pending", {
        method: "POST",
        body: JSON.stringify({ carNumber: car.carNumber }),
      });
      await loadCars();
    } catch (err: any) {
      setError(err?.message || "שגיאה בביטול בקשת אישור");
    } finally {
      setSaving(false);
    }
  };

  const openEndModal = async (car: TestDriveCar) => {
    setSelectedCar(car);
    setEndFormAnswers({});
    try {
      if (car.nextFeedbackFormVersion) {
        const data = await apiRequest<{ form: ExperienceForm }>(
          `/test-drive/experience-form/${car.nextFeedbackFormVersion}`
        );
        setEndDriveForm(data.form);
      } else {
        const form = await loadDefaultForm();
        setEndDriveForm(form);
      }
    } catch {
      const form = await loadDefaultForm();
      setEndDriveForm(form);
    }
    setEndModalOpen(true);
  };

  const openFeedbackModal = async (car: TestDriveCar) => {
    setSelectedCar(car);
    if (!forms.length) {
      await loadFormVersions();
    }
    const formsToUse = forms.length ? forms : (await apiRequest<{ forms: ExperienceForm[] }>("/test-drive/experience-form/versions")).forms || [];
    const referencedForm = formsToUse.find(
      (form) => form.version === car.nextFeedbackFormVersion
    );
    const defaultFormPick = formsToUse.find((form) => form.isDefault) || formsToUse[0] || null;
    setFeedbackFormVersion(
      referencedForm?.version ?? (defaultFormPick ? defaultFormPick.version : null)
    );
    setFeedbackModalOpen(true);
  };

  const openDeleteModal = (car: TestDriveCar) => {
    setSelectedCar(car);
    setDeleteModalOpen(true);
  };

  const openHistoryModal = (car: TestDriveCar) => {
    setSelectedCar(car);
    setHistorySearch("");
    setHistoryModalOpen(true);
  };

  const closeHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistorySearch("");
  };

  const openDeleteHistorySessionModal = (session: TestDriveSession) => {
    if (!selectedCar) {
      return;
    }
    const userId = getHistorySessionUserId(session);
    if (!userId || !session.startDate) {
      setError("לא ניתן למחוק רשומה זו");
      return;
    }
    setHistorySessionPendingDelete({
      userId,
      startDate: new Date(session.startDate).toISOString(),
      driverName: session.name,
      isActive: isActiveHistorySession(selectedCar, session),
    });
    setDeleteHistorySessionModalOpen(true);
  };

  const sendFeedbackReminder = async (session: TestDriveSession) => {
    if (!selectedCar) {
      return;
    }
    const userId = getHistorySessionUserId(session);
    if (!userId || !session.startDate) {
      setError("לא ניתן לשלוח תזכורת לרשומה זו");
      return;
    }

    const sessionKey = feedbackReminderSessionKey(session);
    setFeedbackReminderSendingKey(sessionKey);
    setError("");
    try {
      await apiRequest<{ ok: boolean }>("/test-drive/feedback-reminder", {
        method: "POST",
        body: JSON.stringify({
          carNumber: selectedCar.carNumber,
          userId,
          startDate: new Date(session.startDate).toISOString(),
        }),
      });
    } catch (err: any) {
      setError(err?.message || "שליחת תזכורת למילוי משוב נכשלה");
    } finally {
      setFeedbackReminderSendingKey(null);
    }
  };

  const deleteHistorySession = async () => {
    if (!selectedCar || !historySessionPendingDelete) {
      return;
    }
    setSaving(true);
    setError("");
    try {
      const data = await apiRequest<{ car: TestDriveCar }>("/test-drive/history", {
        method: "DELETE",
        body: JSON.stringify({
          carNumber: selectedCar.carNumber,
          userId: historySessionPendingDelete.userId,
          startDate: historySessionPendingDelete.startDate,
        }),
      });
      setDeleteHistorySessionModalOpen(false);
      setHistorySessionPendingDelete(null);
      setSelectedCar(data.car);
      setCars((prev) =>
        prev.map((car) => (car._id === data.car._id ? data.car : car)),
      );
    } catch (err: any) {
      setError(err?.message || "מחיקת נסיעה מההיסטוריה נכשלה");
      setDeleteHistorySessionModalOpen(false);
      setHistorySessionPendingDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadHistoryExcel = () => {
    if (!selectedCar?.testDriveHistory?.length) {
      alert("אין היסטוריה להורדה");
      return;
    }

    const formatDate = (value?: string | null) =>
      value ? new Date(value).toLocaleString("he-IL") : "";

    const formatAnswerValue = (answer: FeedbackAnswer) =>
      answer.selectedOptionLabel || answer.freeText || answer.selectedOptionValue || "";

    const questionColumnHeaders = new Map<string, string>();
    const usedHeaders = new Set<string>();

    for (const session of selectedCar.testDriveHistory) {
      for (const answer of session.experienceFeedback?.answers || []) {
        if (questionColumnHeaders.has(answer.questionKey)) {
          continue;
        }
        let header = answer.questionLabel.trim() || answer.questionKey;
        if (usedHeaders.has(header)) {
          header = `${header} (${answer.questionKey})`;
        }
        usedHeaders.add(header);
        questionColumnHeaders.set(answer.questionKey, header);
      }
    }

    const questionKeys = Array.from(questionColumnHeaders.keys());

    const rows = selectedCar.testDriveHistory.map((session) => {
      const feedback = session.experienceFeedback;
      const answersByKey = new Map(
        (feedback?.answers || []).map((answer) => [
          answer.questionKey,
          formatAnswerValue(answer),
        ]),
      );

      const row: Record<string, string | number> = {
        "מספר רכב": selectedCar.carNumber,
        יצרן: selectedCar.manufacturer || "",
        דגם: selectedCar.model || "",
        "שם נהג": session.name,
        טלפון: session.phone,
        "ת.ז.": session.personalId || "",
        התחלה: formatDate(session.startDate),
        סיום: session.endDate ? formatDate(session.endDate) : "נסיעה פעילה",
        "זמן כולל": formatTotalDaysHeld(session.startDate, session.endDate),
        "משוב הוגש": feedback?.submittedAt ? "כן" : "לא",
        "גרסת משוב": feedback?.formVersion ?? "",
        "תאריך הגשת משוב": formatDate(feedback?.submittedAt),
      };

      for (const questionKey of questionKeys) {
        const header = questionColumnHeaders.get(questionKey) ?? questionKey;
        row[header] = answersByKey.get(questionKey) ?? "";
      }

      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "היסטוריה");
    const safeCarNumber = selectedCar.carNumber.replace(/[^\w\u0590-\u05FF-]+/g, "_");
    XLSX.writeFile(workbook, `test_drive_history_${safeCarNumber}.xlsx`);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setAddModalError("");
  };

  const openExportHistoryModal = (car?: TestDriveCar) => {
    const today = toLocalDateInputValue(new Date());
    const scopeCars = car ? [car] : cars;
    setExportCar(car ?? null);
    setExportFrom(oldestHistoryStartDateInput(scopeCars));
    setExportTo(today);
    setExportModalOpen(true);
  };

  const closeExportHistoryModal = () => {
    setExportModalOpen(false);
    setExportCar(null);
  };

  const downloadHistoryExportExcel = async () => {
    if (!exportFrom || !exportTo) {
      alert("יש לבחור מתאריך ועד תאריך");
      return;
    }
    if (exportFrom > exportTo) {
      alert("מתאריך חייב להיות לפני או שווה לעד תאריך");
      return;
    }
    setExportingHistory(true);
    try {
      const params = new URLSearchParams({ from: exportFrom, to: exportTo });
      if (exportCar?.carNumber) {
        params.set("carNumber", exportCar.carNumber);
      }
      const response = await fetch(
        `${config.backendUrl}/test-drive/history/export-excel?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || "ייצוא נכשל");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const carPart = exportCar?.carNumber
        ? `_${exportCar.carNumber.replace(/[^\w\u0590-\u05FF-]+/g, "_")}`
        : "";
      a.download = `test_drive_history${carPart}_${exportFrom}_${exportTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setExportModalOpen(false);
      setExportCar(null);
    } catch (err: any) {
      alert(err?.message || "שגיאה בייצוא היסטוריית נסיעות מבחן");
    } finally {
      setExportingHistory(false);
    }
  };

  const openAddModal = () => {
    setAddModalError("");
    setAddModalOpen(true);
  };

  const addCar = async () => {
    if (!addForm.carNumber.trim()) {
      setAddModalError("יש להזין מספר רכב");
      return;
    }
    setSaving(true);
    setAddModalError("");
    try {
      await apiRequest("/test-drive/cars", {
        method: "POST",
        body: JSON.stringify({
          carNumber: addForm.carNumber.trim(),
          manufacturer: addForm.manufacturer.trim() || null,
          model: addForm.model.trim() || null,
        }),
      });
      closeAddModal();
      setAddForm({ carNumber: "", manufacturer: "", model: "" });
      await loadCars();
    } catch (err: any) {
      setAddModalError(err?.message || "שגיאה בהוספת רכב");
    } finally {
      setSaving(false);
    }
  };

  const startDrive = async () => {
    if (!selectedCar) return;
    if (!startForm.name.trim() || !startForm.phone.trim()) {
      setStartModalError("יש למלא שם וטלפון נהג");
      return;
    }
    setSaving(true);
    setStartModalError("");
    try {
      await apiRequest("/test-drive/start-request", {
        method: "POST",
        body: JSON.stringify({
          carNumber: selectedCar.carNumber,
          manufacturer: selectedCar.manufacturer,
          model: selectedCar.model,
          driver: {
            name: startForm.name.trim(),
            displayName: startForm.name.trim(),
            phone: startForm.phone.trim(),
            personalId: startForm.personalId.trim() || null,
          },
        }),
      });
      closeStartModal();
      await loadCars();
    } catch (err: any) {
      setStartModalError(err?.message || "שגיאה בשליחת בקשת אישור");
    } finally {
      setSaving(false);
    }
  };

  const endDrive = async () => {
    if (!selectedCar) return;
    const questions = endDriveForm?.questions || [];
    const feedbackAnswers = questions
      .map((question) => ({
        questionKey: question.key,
        selectedOptionValue: endFormAnswers[question.key]?.selectedOptionValue,
        freeText: endFormAnswers[question.key]?.freeText || "",
      }))
      .filter((answer) => {
        if (!answer.questionKey) return false;
        if (answer.selectedOptionValue) return true;
        return Boolean(answer.freeText && answer.freeText.trim());
      });

    setSaving(true);
    setError("");
    try {
      await apiRequest("/test-drive/end", {
        method: "POST",
        body: JSON.stringify({
          carNumber: selectedCar.carNumber,
          feedbackAnswers,
        }),
      });
      setEndModalOpen(false);
      await loadCars();
    } catch (err: any) {
      setError(err?.message || "שגיאה בסיום נסיעת מבחן");
    } finally {
      setSaving(false);
    }
  };

  const deleteCar = async () => {
    if (!selectedCar) return;
    setSaving(true);
    setError("");
    try {
      await apiRequest("/test-drive/cars", {
        method: "DELETE",
        body: JSON.stringify({ carNumber: selectedCar.carNumber }),
      });
      setDeleteModalOpen(false);
      await loadCars();
    } catch (err: any) {
      setError(err?.message || "מחיקת רכב לא הצליחה");
      setDeleteModalOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const saveFeedbackToCar = async () => {
    if (!selectedCar) return;
    if (!feedbackFormVersion) {
      setError("יש לבחור גרסת טופס משוב");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const data = await apiRequest<{ car: TestDriveCar }>("/test-drive/feedback", {
        method: "POST",
        body: JSON.stringify({
          carNumber: selectedCar.carNumber,
          formVersion: feedbackFormVersion,
        }),
      });
      const updatedCar = data.car;
      setCars((prev) =>
        prev.map((car) =>
          car.carNumber === updatedCar.carNumber ? updatedCar : car
        )
      );
      setSelectedCar(updatedCar);
      setFeedbackModalOpen(false);
      await loadCars();
    } catch (err: any) {
      setError(err?.message || "שמירת משוב לרכב נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const setQuestionOption = (questionKey: string, selectedOptionValue: string) => {
    setEndFormAnswers((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        selectedOptionValue,
      },
    }));
  };

  const setQuestionFreeText = (questionKey: string, freeText: string) => {
    setEndFormAnswers((prev) => ({
      ...prev,
      [questionKey]: {
        ...(prev[questionKey] || {}),
        freeText,
      },
    }));
  };

  return (
    <div className={styles.container}>
      {error ? <p className={styles.error}>{error}</p> : null}
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title} style={headingStyle}>
            טבלת נסיעות מבחן
          </h1>
          <Chip color="gray" size="small">
            סה״כ רכבים: {filteredCars.length}
          </Chip>
        </div>

        <div className={styles.toolbar}>
          <Button
            label={editorOpen ? "סגור עריכת משובים" : "עריכת משובים"}
            color="outlineSecondary"
            size="medium"
            width={'auto'}
            icon={<FeedbackEditIcon style={{ display: "block", flexShrink: 0 }} />}
            iconPosition="left"
            onClick={async () => {
              const next = !editorOpen;
              setEditorOpen(next);
              if (next) {
                await loadFormVersions();
              }
            }}
          />
        </div>

        <AnimatePresence initial={false}>
          {editorOpen ? (
            <motion.div
              key="feedback-editor-panel"
              className={styles.feedbackEditorReveal}
              initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
              animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
              exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            >
              <TestDriveFeedbackEditor
                formsError={formsError}
                formsLoading={formsLoading}
                forms={forms}
                selectedVersion={selectedVersion}
                selectedForm={selectedForm}
                editorQuestions={editorQuestions}
                editorFormName={editorFormName}
                saving={saving}
                tableCellStyle={tableCellStyle}
                onSelectVersion={selectVersion}
                onUpdateFormName={setEditorFormName}
                onCreateNewVersion={createNewVersion}
                onDuplicateCurrentVersion={duplicateCurrentVersion}
                onAddQuestion={addQuestion}
                onSetDefaultVersion={setDefaultVersion}
                onOpenDeleteFormVersionModal={openDeleteFormVersionModal}
                onSaveVersion={saveVersion}
                onUpdateQuestion={updateQuestion}
                onDeleteQuestion={deleteQuestion}
                onReorderQuestions={reorderQuestions}
                onUpdateOption={updateOption}
                onDeleteOption={deleteOption}
                onAddOption={addOption}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div className={styles.toolbar}>
          <Input
            placeholder="חיפוש מספר רכב / יצרן / דגם"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            color="outline"
            size="medium"
            width={260}
            rtl
          />
          <DropdownSelect
            name="test-drive-status-filter"
            values={[selectedStatusFilter]}
            onChange={handleStatusFilterChange}
            options={[...STATUS_FILTER_OPTIONS]}
            color="outline"
            size="medium"
            width={140}
          />
          <Button
            label="איפוס"
            color="secondary"
            size="medium"
            width={90}
            icon={<ResetIcon style={{ display: "block", flexShrink: 0 }} />}
            iconPosition="left"
            onClick={resetFilters}
          />
          <Button
            label="ייצוא"
            color="outline"
            size="medium"
            width={100}
            icon={<ExcelIcon size={16} />}
            iconPosition="left"
            onClick={openExportHistoryModal}
          />
          <Button
            label="הוספת רכב"
            color="primary"
            size="medium"
            width={120}
            icon={<AddIcon style={{ display: "block", flexShrink: 0 }} />}
            iconPosition="left"
            onClick={openAddModal}
          />
        </div>

        <div className={styles.tableWrap}>
          <div className={styles.tableScroll}>
            <Table className={styles.superTable}>
              <Thead>
                <Tr className={styles.headRow}>
                  <Th style={tableCellStyle}>מספר רכב</Th>
                  <Th style={tableCellStyle}>יצרן</Th>
                  <Th style={tableCellStyle}>דגם</Th>
                  <Th style={tableCellStyle}>סטטוס</Th>
                  <Th style={tableCellStyle}>נהג פעיל</Th>
                  <Th style={tableCellStyle}>משוב משוייך</Th>
                  <Th style={tableCellStyle}>פעולות</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr className={styles.bodyRow}>
                    <Td colSpan={7} className={styles.empty} style={tableCellStyle}>
                      טוען...
                    </Td>
                  </Tr>
                ) : filteredCars.length === 0 ? (
                  <Tr className={styles.bodyRow}>
                    <Td colSpan={7} className={styles.empty} style={tableCellStyle}>
                      לא נמצאו רכבים
                    </Td>
                  </Tr>
                ) : (
                  filteredCars.map((car) => (
                    <Tr key={car._id} className={styles.bodyRow}>
                      <Td style={tableCellStyle}>{car.carNumber}</Td>
                      <Td style={tableCellStyle}>{car.manufacturer || "-"}</Td>
                      <Td style={tableCellStyle}>{car.model || "-"}</Td>
                      <Td style={tableCellStyle}>
                        <Chip
                          color={
                            car.status === "available"
                              ? "green"
                              : car.status === "pending"
                                ? "gray"
                                : "orange"
                          }
                          size="small"
                          width={car.status === "pending" ? 100 : 70}
                        >
                          {car.status === "available"
                            ? "פנוי"
                            : car.status === "pending"
                              ? "ממתין לאישור"
                              : "בתפוסה"}
                        </Chip>
                      </Td>
                      <Td style={tableCellStyle}>
                        {car.currentDriver ? `${car.currentDriver.name} (${car.currentDriver.phone})` : "-"}
                      </Td>
                      <Td style={tableCellStyle}>
                        {getFormLabelByVersion(car.nextFeedbackFormVersion)}
                      </Td>
                      <Td style={tableCellStyle}>
                        <TestDriveCarRowActions
                          car={car}
                          onStartTestDrive={() => openStartModal(car)}
                          onForceStartWithoutApproval={() => forceStartWithoutApproval(car)}
                          onCancelPending={() => cancelPendingDrive(car)}
                          onEnd={() => openEndModal(car)}
                          onFeedback={() => openFeedbackModal(car)}
                          onHistory={() => openHistoryModal(car)}
                          onExport={() => openExportHistoryModal(car)}
                          onDelete={() => openDeleteModal(car)}
                        />
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </div>
        </div>
      </div>

      <Dialog
        open={addModalOpen}
        onClose={closeAddModal}
        fullWidth
        maxWidth="xs"
        slotProps={dialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary, mb: 1 }}>
            הוספת רכב לנסיעות מבחן
          </Typography>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>מספר רכב</span>
            <Input value={addForm.carNumber} onChange={(e) => setAddForm((p) => ({ ...p, carNumber: e.target.value }))} color="outline" size="medium" rtl />
          </div>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>יצרן</span>
            <Input value={addForm.manufacturer} onChange={(e) => setAddForm((p) => ({ ...p, manufacturer: e.target.value }))} color="outline" size="medium" rtl />
          </div>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>דגם</span>
            <Input value={addForm.model} onChange={(e) => setAddForm((p) => ({ ...p, model: e.target.value }))} color="outline" size="medium" rtl />
          </div>
          <ErrorMessage message={addModalError} size="small" />
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2 }}>
          <Button label="שמור" color="primary" size="medium" width={90} onClick={addCar} disabled={saving} />
          <Button label="ביטול" color="outline" size="medium" width={90} onClick={closeAddModal} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={exportModalOpen}
        onClose={closeExportHistoryModal}
        fullWidth
        maxWidth="xs"
        slotProps={dialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary, mb: 1 }}>
            ייצוא היסטוריית נסיעות מבחן
            {exportCar ? ` • ${exportCar.carNumber}` : ""}
          </Typography>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>מתאריך</span>
            <Input
              type="date"
              value={exportFrom}
              onChange={(e) => setExportFrom(e.target.value)}
              color="outline"
              size="medium"
              rtl
            />
          </div>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>עד תאריך</span>
            <Input
              type="date"
              value={exportTo}
              onChange={(e) => setExportTo(e.target.value)}
              color="outline"
              size="medium"
              rtl
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2 }}>
          <Button
            label="ייצוא"
            color="primary"
            size="medium"
            width={100}
            icon={<ExcelIcon size={16} />}
            iconPosition="left"
            onClick={downloadHistoryExportExcel}
            disabled={exportingHistory}
          />
          <Button
            label="ביטול"
            color="outline"
            size="medium"
            width={90}
            onClick={closeExportHistoryModal}
            disabled={exportingHistory}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={startModalOpen}
        onClose={closeStartModal}
        fullWidth
        maxWidth="xs"
        slotProps={dialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary, mb: 1 }}>
            התחלת נסיעת מבחן {selectedCar ? `• ${selectedCar.carNumber}` : ""}
          </Typography>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>טלפון</span>
            <Input value={startForm.phone} onChange={(e) => setStartForm((p) => ({ ...p, phone: e.target.value }))} color="outline" size="medium" rtl />
          </div>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>שם נהג</span>
            <Input value={startForm.name} onChange={(e) => setStartForm((p) => ({ ...p, name: e.target.value }))} color="outline" size="medium" rtl />
          </div>
          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>ת.ז. (אופציונלי)</span>
            <Input value={startForm.personalId} onChange={(e) => setStartForm((p) => ({ ...p, personalId: e.target.value }))} color="outline" size="medium" rtl />
          </div>
          <ErrorMessage message={startModalError} size="small" />
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2, flexWrap: "wrap" }}>
          <Button
            label="שלח לאישור נהג"
            color="primary"
            size="medium"
            width={140}
            onClick={startDrive}
            disabled={saving}
          />
          <Button label="ביטול" color="outline" size="medium" width={90} onClick={closeStartModal} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={endModalOpen}
        onClose={() => setEndModalOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={dialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary, mb: 1 }}>
            סיום נסיעת מבחן ומשוב {selectedCar ? `• ${selectedCar.carNumber}` : ""}
          </Typography>
          {(endDriveForm?.questions || []).map((question) => {
            const selectedOption = question.options?.find(
              (opt) => opt.value === endFormAnswers[question.key]?.selectedOptionValue
            );
            return (
              <div className={styles.fieldGroup} key={question.key}>
                <span className={styles.fieldLabel} style={tableCellStyle}>
                  {question.label}
                </span>
                {question.type === "singleSelect" ? (
                  <div className={styles.radioGroup}>
                    {(question.options || []).map((option) => (
                      <label key={option.value} className={styles.radioOption} style={tableCellStyle}>
                        <input
                          type="radio"
                          name={question.key}
                          value={option.value}
                          checked={endFormAnswers[question.key]?.selectedOptionValue === option.value}
                          onChange={() => setQuestionOption(question.key, option.value)}
                        />
                        {option.label}
                      </label>
                    ))}
                    {selectedOption?.allowsFreeText ? (
                      <Input
                        placeholder="פרט/י..."
                        value={endFormAnswers[question.key]?.freeText || ""}
                        onChange={(e) => setQuestionFreeText(question.key, e.target.value)}
                        color="outline"
                        size="medium"
                        rtl
                      />
                    ) : null}
                  </div>
                ) : question.type === "starRating" ? (
                  <div className={styles.radioGroup}>
                    {STAR_RATING_OPTIONS.map((option) => (
                      <label key={option.value} className={styles.radioOption} style={tableCellStyle}>
                        <input
                          type="radio"
                          name={question.key}
                          value={option.value}
                          checked={endFormAnswers[question.key]?.selectedOptionValue === option.value}
                          onChange={() => setQuestionOption(question.key, option.value)}
                        />
                        {option.label}
                      </label>
                    ))}
                  </div>
                ) : (
                  <Input
                    value={endFormAnswers[question.key]?.freeText || ""}
                    onChange={(e) => setQuestionFreeText(question.key, e.target.value)}
                    color="outline"
                    size="medium"
                    rtl
                  />
                )}
              </div>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2 }}>
          <Button label="סיים ושמור" color="primary" size="medium" width={120} onClick={endDrive} disabled={saving} />
          <Button label="ביטול" color="outline" size="medium" width={90} onClick={() => setEndModalOpen(false)} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={feedbackDialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary, mb: 1, marginBottom: 4 }}>
            משוב נסיעת מבחן {selectedCar ? `• ${selectedCar.carNumber}` : ""}
          </Typography>


          <div className={styles.fieldGroup}>
            <span className={styles.fieldLabel} style={tableCellStyle}>שיוך משוב לרכב</span>
            <DropdownSelect
              name="feedback-form-version"
              values={selectedFeedbackForm ? [selectedFeedbackForm] : []}
              onChange={(selected: { value: string }[]) => {
                const version = Number(selected[0]?.value);
                setFeedbackFormVersion(Number.isFinite(version) ? version : null);
              }}
              options={feedbackFormOptions}
              color="outline"
              size="medium"
              width={300}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2 }}>
          <Button
            label="שמור משוב לרכב"
            color="primary"
            size="medium"
            width={130}
            onClick={saveFeedbackToCar}
            disabled={saving || !selectedCar || !feedbackFormVersion}
          />
          <Button label="סגור" color="outline" size="medium" width={90} onClick={() => setFeedbackModalOpen(false)} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyModalOpen}
        onClose={closeHistoryModal}
        fullWidth
        maxWidth="md"
        scroll="paper"
        slotProps={historyDialogSlotProps}
      >
        <DialogActions
          sx={{
            direction: "rtl",
            p: 2,
            gap: 2,
            m: 0,
            flexShrink: 0,
            backgroundColor: "#eee",
            borderBottom: `1px solid ${tokens.colors.border}`,
            justifyContent: "flex-start",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <Button
            iconOnly
            label="סגור"
            icon={<XCloseIcon style={{ display: "block", flexShrink: 0 }} />}
            color="outlineSecondary"
            size="medium"
            onClick={closeHistoryModal}
          />
          <div className={styles.historyToolbarEnd}>
            {selectedCar?.testDriveHistory?.length ? (
              <Input
                placeholder="חיפוש לפי שם / טלפון / ת.ז."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                color="outline"
                size={isMobile ? "small" : "medium"}
                width={isMobile ? undefined : 220}
                mobileWidth={isMobile ? 150 : undefined}
                rtl
              />
            ) : null}
            <Button
              label={isMobile ? "הורד" : "הורד אקסל"}
              color="secondary"
              size={isMobile ? "small" : "medium"}
              width="auto"
              icon={<ExcelIcon size={isMobile ? 14 : 16} />}
              iconPosition="left"
              onClick={handleDownloadHistoryExcel}
              disabled={!selectedCar?.testDriveHistory?.length}
            />
          </div>
        </DialogActions>
        <DialogContent className={styles.historyDialogContent} sx={{ direction: "rtl", pt: 2 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary, mb: 1 }}>
            היסטוריית נסיעות מבחן {selectedCar ? `• ${selectedCar.carNumber}` : ""}
          </Typography>
          {!selectedCar?.testDriveHistory?.length ? (
            <Typography sx={{ ...tokens.typography.body, color: tokens.colors.text.primary }}>
              אין היסטוריה לרכב זה
            </Typography>
          ) : filteredHistorySessions.length === 0 ? (
            <Typography sx={{ ...tokens.typography.body, color: tokens.colors.text.primary }}>
              לא נמצאו נסיעות התואמות לחיפוש
            </Typography>
          ) : (
            filteredHistorySessions.map((session, idx) => (
              <div
                key={`${getHistorySessionUserId(session)}-${session.startDate}-${idx}`}
                className={styles.feedbackBlock}
              >
                <div className={styles.feedbackBlockHeader}>
                  <p className={styles.feedbackAnswer} style={tableCellStyle}>
                    <strong>נהג:</strong> {session.name} ({session.phone})
                    {isActiveHistorySession(selectedCar, session) ? " — נסיעה פעילה" : ""}
                  </p>
                  <Button
                    label="מחק"
                    color="outlineAccent"
                    size="small"
                    width={65}
                    onClick={() => openDeleteHistorySessionModal(session)}
                    disabled={saving}
                  />
                </div>
                {session.personalId ? (
                  <p className={styles.feedbackAnswer} style={tableCellStyle}>
                    <strong>ת.ז.:</strong> {session.personalId}
                  </p>
                ) : null}
                <p className={styles.feedbackAnswer} style={tableCellStyle}>
                  <strong>התחלה:</strong>{" "}
                  {session.startDate
                    ? new Date(session.startDate).toLocaleString("he-IL")
                    : "-"}
                </p>
                <p className={styles.feedbackAnswer} style={tableCellStyle}>
                  <strong>סיום:</strong>{" "}
                  {session.endDate
                    ? new Date(session.endDate).toLocaleString("he-IL")
                    : "נסיעה פעילה"}
                </p>
                <p className={styles.feedbackAnswer} style={tableCellStyle}>
                  <strong>זמן כולל:</strong>{" "}
                  {formatTotalDaysHeld(session.startDate, session.endDate)}
                </p>
                {session.experienceFeedback ? (
                  session.experienceFeedback.submittedAt ? (
                    <>
                      <p className={styles.feedbackAnswer} style={tableCellStyle}>
                        <strong>משוב:</strong> הוגש (גרסה {session.experienceFeedback.formVersion})
                      </p>

                      <p className={styles.answersTitle} >
                        תשבות לשאלות המשוב
                      </p>
                      {session.experienceFeedback.answers?.length ? (
                        session.experienceFeedback.answers.map((answer, answerIdx) => (
                          <div
                            key={`${session.userId || "session"}-${idx}-answer-${answerIdx}`}
                            className={styles.feedbackBlockQuestion}
                          >
                            <p className={styles.feedbackAnswerTitle} style={tableCellStyle}>
                              <strong>{answer.questionLabel}</strong>
                            </p>
                            {answer.selectedOptionLabel ? (
                              <p className={styles.feedbackAnswer} style={tableCellStyle}>
                                {answer.selectedOptionLabel}
                              </p>
                            ) : null}
                            {answer.freeText ? (
                              <p className={styles.feedbackAnswer} style={tableCellStyle}>
                                {answer.freeText}
                              </p>
                            ) : null}
                            {!answer.selectedOptionLabel && !answer.freeText ? (
                              <p className={styles.feedbackAnswer} style={tableCellStyle}>
                                ללא תשובה
                              </p>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        <p className={styles.feedbackAnswer} style={tableCellStyle}>
                          אין תשובות משוב להצגה
                        </p>
                      )}
                    </>
                  ) : (
                    <motion.div
                      className={styles.feedbackReminderRow}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <p className={styles.feedbackAnswer} style={tableCellStyle}>
                        <strong>משוב:</strong> לא הוגש
                      </p>
                      {selectedCar && canSendFeedbackReminder(selectedCar, session) ? (
                        <Button
                          label="שלח תזכורת למילוי משוב"
                          color="outline"
                          size="small"
                          width="auto"
                          onClick={() => sendFeedbackReminder(session)}
                          disabled={
                            saving ||
                            feedbackReminderSendingKey === feedbackReminderSessionKey(session)
                          }
                        />
                      ) : null}
                    </motion.div>
                  )
                ) : (
                  <motion.div
                    className={styles.feedbackReminderRow}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className={styles.feedbackAnswer} style={tableCellStyle}>
                      <strong>משוב:</strong> לא הוגש
                    </p>
                    {selectedCar && canSendFeedbackReminder(selectedCar, session) ? (
                      <Button
                        label="שלח תזכורת למילוי משוב"
                        color="outline"
                        size="small"
                        width="auto"
                        onClick={() => sendFeedbackReminder(session)}
                        disabled={
                          saving ||
                          feedbackReminderSendingKey === feedbackReminderSessionKey(session)
                        }
                      />
                    ) : null}
                  </motion.div>
                )}
              </div>
            ))
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        fullWidth
        maxWidth="xs"
        slotProps={dialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary }}>
            להסיר את הרכב {selectedCar?.carNumber} מרשימת נסיעות המבחן?
          </Typography>
          <Typography
            sx={{
              ...tokens.typography.body,
              color: tokens.colors.text.secondary,
              mt: 1,
            }}
          >
            הרכב יוסתר מהרשימה; היסטוריית הנסיעות תישמר במערכת.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2 }}>
          <Button label="מחק" color="primary" size="medium" width={90} onClick={deleteCar} disabled={saving} />
          <Button label="ביטול" color="outline" size="medium" width={90} onClick={() => setDeleteModalOpen(false)} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteHistorySessionModalOpen}
        onClose={() => {
          setDeleteHistorySessionModalOpen(false);
          setHistorySessionPendingDelete(null);
        }}
        fullWidth
        maxWidth="xs"
        slotProps={dialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary }}>
            למחוק את נסיעת המבחן של {historySessionPendingDelete?.driverName}?
          </Typography>
          {historySessionPendingDelete?.isActive ? (
            <Typography
              sx={{
                ...tokens.typography.bodySmall,
                color: tokens.colors.text.secondary,
                mt: 1,
              }}
            >
              זו נסיעה פעילה — הרכב יוחזר לסטטוס פנוי.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2 }}>
          <Button
            label="מחק"
            color="primary"
            size="medium"
            width={90}
            onClick={deleteHistorySession}
            disabled={saving}
          />
          <Button
            label="ביטול"
            color="outline"
            size="medium"
            width={90}
            onClick={() => {
              setDeleteHistorySessionModalOpen(false);
              setHistorySessionPendingDelete(null);
            }}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteFormVersionModalOpen}
        onClose={() => {
          setDeleteFormVersionModalOpen(false);
          setFormVersionPendingDelete(null);
        }}
        fullWidth
        maxWidth="xs"
        slotProps={dialogSlotProps}
      >
        <DialogContent sx={{ direction: "rtl", pt: 3 }}>
          <Typography sx={{ ...tokens.typography.h6, color: tokens.colors.text.primary }}>
            למחוק את {getFormLabelByVersion(formVersionPendingDelete)}? רכבים ששויכו לגרסה יעברו לברירת המחדל החדשה.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ direction: "rtl", p: 2, gap: 2 }}>
          <Button
            label="מחק"
            color="primary"
            size="medium"
            width={90}
            onClick={deleteFormVersion}
            disabled={saving}
          />
          <Button
            label="ביטול"
            color="outline"
            size="medium"
            width={90}
            onClick={() => {
              setDeleteFormVersionModalOpen(false);
              setFormVersionPendingDelete(null);
            }}
          />
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TestDriveList;
